<template>
  <header
    id="header"
    class="bg-surface-default-low sticky top-0 z-10 transition-all duration-200"
    :class="{ '-translate-y-full': headerIsHidden }"
  >
    <div
      class="flex flex-wrap items-center justify-between gap-16 px-20 py-16 lg:gap-56 lg:px-32 lg:py-20"
    >
      <div class="flex-1 lg:hidden">
        <BurgerMenuIcon data-test="mobile-burger-menu" />
      </div>
      <TheLogo />

      <div class="hidden lg:flex lg:gap-24">
        <RevLink
          v-for="link in mainLinks"
          :key="link.name"
          data-test="footer-link"
          :to="{
            name: link.name,
            params: link.params,
          }"
          :underlined="false"
          @click="trackClick({ name: link.trackingName, zone: 'header' })"
        >
          <span class="body-2-bold">
            {{ i18n(link.title) }}
          </span>
        </RevLink>
      </div>

      <div class="order-last w-full grow lg:order-none lg:flex-1">
        <SearchBar />
      </div>

      <div class="flex flex-1 items-center justify-end lg:grow-0">
        <AdminMenu v-if="isAdmin" data-test="icon-admin" />

        <UserIconAndSubMenu v-else />

        <CartIconAndBubble data-test="icon-cart" />
      </div>
    </div>

    <TopSalesBar class="border-static-default-low hidden border-b lg:flex" />

    <BurgerMenu
      v-if="burgerMenuState === 'open'"
      :class="{ 'lg:hidden': universeNavigation }"
    />
  </header>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useUserStore } from '@backmarket/nuxt-module-oauth/useUserStore'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevLink } from '@ds/components/Link'

import { ABOUT_US } from '~/scopes/about-us/routes'
import { CMS } from '~/scopes/cms/routes-names'
import { HELP_CENTER } from '~/scopes/help-center/routes'
import SearchBar from '~/scopes/search/SearchBar/SearchBar.vue'

import { useHideOnScroll } from '../composables/useHideOnScroll'
import { useNavigation } from '../composables/useNavigation'

import AdminMenu from './AdminMenu.vue'
import BurgerMenu from './BurgerMenu.vue'
import BurgerMenuIcon from './BurgerMenuIcon.vue'
import CartIconAndBubble from './CartIconAndBubble.vue'
import translations from './Header.translations'
import TheLogo from './TheLogo.vue'
import TopSalesBar from './TopSalesBar.vue'
import UserIconAndSubMenu from './UserIconAndSubMenu.vue'

const userStore = useUserStore()

const i18n = useI18n()

const isAdmin =
  userStore.user.isStaff ||
  userStore.user.isShadowingClient ||
  userStore.user.isShadowingMerchant

const { trackClick } = useTracking()

const { features }: { features: { buyback: { enabled: boolean } } } =
  useMarketplace()
const locale = useI18nLocale()

const aboutUsLink = {
  name: ABOUT_US,
  title: translations.aboutUs,
  trackingName: 'trustedLink',
  params: {
    locale,
  },
}
const buybackLink = {
  name: CMS.BUYBACK,
  params: {
    pageName: 'home',
    locale,
  },
  title: translations.sellProduct,
  trackingName: 'buyback',
}

const helpCenterLink = {
  name: HELP_CENTER.HOME,
  title: translations.help,
  trackingName: 'helpContact',
  params: {
    locale,
  },
}
const mainLinks = [
  aboutUsLink,
  ...(features.buyback.enabled ? [buybackLink] : []),
  helpCenterLink,
]

const { hidden: headerIsHidden } = useHideOnScroll()
const universeNavigation = false

const { state: burgerMenuState, toggle } = useNavigation()
function closeMenu({ key }: KeyboardEvent) {
  if (key === 'Escape') {
    if (burgerMenuState.value === 'open') toggle()
  }
}
onMounted(() => {
  // Hydration marker for PlayWright and Cie, related https://spin.atomicobject.com/2023/07/19/hydration-sveltekit-tests/
  document.body.classList.add('header-mounted')

  window.addEventListener('keydown', closeMenu)
})
onUnmounted(() => {
  document.body.classList.remove('header-mounted')

  window.removeEventListener('keydown', closeMenu)
})
</script>
